import { useEffect } from 'react';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useDeckStore } from '../store/deckStore';
import { CANVAS } from '../lib/brand';
import { SlideElementView } from './elements/SlideElements';
import { slideSurfaceStyle } from '../lib/slideStyle';

const overlayButton = (t: Theme) => ({
  color: t.palette.common.white,
  bgcolor: alpha(t.palette.common.white, 0.1),
  '&:hover': { bgcolor: alpha(t.palette.common.white, 0.2) },
  '&.Mui-disabled': { color: alpha(t.palette.common.white, 0.25) },
});

export function PresentMode() {
  const presenting = useDeckStore((s) => s.presenting);
  const setPresenting = useDeckStore((s) => s.setPresenting);
  const slides = useDeckStore((s) => s.slides);
  const activeSlideIndex = useDeckStore((s) => s.activeSlideIndex);
  const setActiveSlide = useDeckStore((s) => s.setActiveSlide);

  const next = () => setActiveSlide(Math.min(activeSlideIndex + 1, slides.length - 1));
  const prev = () => setActiveSlide(Math.max(activeSlideIndex - 1, 0));

  useEffect(() => {
    if (!presenting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPresenting(false);
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setActiveSlide(Math.min(activeSlideIndex + 1, slides.length - 1));
      }
      if (e.key === 'ArrowLeft') {
        setActiveSlide(Math.max(activeSlideIndex - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [presenting, activeSlideIndex, slides.length, setActiveSlide, setPresenting]);

  if (!presenting) return null;
  const slide = slides[activeSlideIndex];
  if (!slide) return null;

  const scale = Math.min(window.innerWidth / CANVAS.width, window.innerHeight / CANVAS.height);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (t) => t.zIndex.modal + 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'common.black',
      }}
    >
      <Tooltip title="Exit (Esc)">
        <IconButton
          onClick={() => setPresenting(false)}
          sx={[overlayButton, { position: 'absolute', right: 16, top: 16, zIndex: 1 }]}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
      <div
        style={{
          width: CANVAS.width * scale,
          height: CANVAS.height * scale,
        }}
      >
        <div
          style={{
            ...slideSurfaceStyle(slide),
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {slide.elements.map((el) => (
            <SlideElementView
              key={el.id}
              el={el}
              selected={false}
              scale={1}
              onSelect={() => undefined}
              onChangeText={() => undefined}
              onDragEnd={() => undefined}
            />
          ))}
        </div>
      </div>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton size="small" onClick={prev} disabled={activeSlideIndex === 0} sx={overlayButton}>
          <ChevronLeftIcon />
        </IconButton>
        <Chip
          label={`${activeSlideIndex + 1} / ${slides.length} · ← → or Space`}
          sx={overlayButton}
        />
        <IconButton
          size="small"
          onClick={next}
          disabled={activeSlideIndex === slides.length - 1}
          sx={overlayButton}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
